// components/LandingPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Download,
    FileText,
    Shield,
    RefreshCw,
    BarChart,
    Database,
    DollarSign,
    Lock,
    Rocket,
    FileCheck,
    Search,
    Palette,
    TrendingUp,
    Clock,
    Target,
    Smartphone,
    CheckCircle,
    Menu,
    X
} from 'lucide-react';

// Importar los logos (ajusta las rutas según tus archivos)
import logoMafy from '../assets/images/logo-mafy.png';
import logoMafyWhite from '../assets/images/logo-mafy-white.png';

const LandingPage = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
                    ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg'
                    : 'bg-transparent'
                }`}>
                <nav className="container mx-auto px-4">
                    <div className="flex items-center justify-between h-16 lg:h-20">
                        {/* Logo - Cambia según el estado del scroll */}
                        <div className="flex items-center space-x-3">
                            <img
                                src={isScrolled ? logoMafy : logoMafyWhite}
                                alt="Mafy"
                                className="h-10 w-10 lg:h-12 lg:w-12 transition-all duration-300"
                            />
                            <span className={`text-2xl lg:text-3xl font-bold transition-colors duration-300 ${isScrolled
                                    ? 'text-primary-600 dark:text-primary-400'
                                    : 'text-white'
                                }`}>
                                Mafy
                            </span>
                        </div>

                        {/* Desktop Navigation - Cambian los colores según el scroll */}
                        <ul className="hidden lg:flex items-center space-x-8">
                            <li>
                                <a href="#inicio" className={`nav-link transition-colors duration-300 ${isScrolled
                                        ? 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                                        : 'text-white hover:text-accent-200'
                                    }`}>
                                    Inicio
                                </a>
                            </li>
                            <li>
                                <a href="#servicios" className={`nav-link transition-colors duration-300 ${isScrolled
                                        ? 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                                        : 'text-white hover:text-accent-200'
                                    }`}>
                                    Servicios
                                </a>
                            </li>
                            <li>
                                <a href="#beneficios" className={`nav-link transition-colors duration-300 ${isScrolled
                                        ? 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                                        : 'text-white hover:text-accent-200'
                                    }`}>
                                    Beneficios
                                </a>
                            </li>
                            <li>
                                <Link to="/api-docs" className={`nav-link transition-colors duration-300 ${isScrolled
                                        ? 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                                        : 'text-white hover:text-accent-200'
                                    }`}>
                                    API
                                </Link>
                            </li>
                            <li>
                                <a href="#contacto" className={`nav-link transition-colors duration-300 ${isScrolled
                                        ? 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                                        : 'text-white hover:text-accent-200'
                                    }`}>
                                    Contacto
                                </a>
                            </li>
                            <li>
                                <Link to="/login" className={`font-medium py-2 px-4 rounded-lg transition-all duration-300 ${isScrolled
                                        ? 'bg-primary-500 hover:bg-primary-600 text-white'
                                        : 'bg-white/20 hover:bg-white/30 text-white border border-white/30 hover:border-white/50'
                                    }`}>
                                    Iniciar Sesión
                                </Link>
                            </li>
                        </ul>

                        {/* Mobile menu button - Cambia color según el scroll */}
                        <button
                            onClick={toggleMenu}
                            className={`lg:hidden p-2 rounded-lg transition-colors duration-300 ${isScrolled
                                    ? 'hover:bg-gray-100 dark:hover:bg-gray-800'
                                    : 'hover:bg-white/10'
                                }`}
                        >
                            {isMenuOpen ? (
                                <X className={`h-6 w-6 transition-colors duration-300 ${isScrolled
                                        ? 'text-gray-600 dark:text-gray-400'
                                        : 'text-white'
                                    }`} />
                            ) : (
                                <Menu className={`h-6 w-6 transition-colors duration-300 ${isScrolled
                                        ? 'text-gray-600 dark:text-gray-400'
                                        : 'text-white'
                                    }`} />
                            )}
                        </button>
                    </div>

                    {/* Mobile Navigation - Fondo adaptativo */}
                    {isMenuOpen && (
                        <div className={`lg:hidden py-4 border-t transition-colors duration-300 ${isScrolled
                                ? 'border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95'
                                : 'border-white/20 bg-black/20 backdrop-blur-md'
                            }`}>
                            <div className="flex flex-col space-y-2">
                                <a href="#inicio" className={`py-3 px-3 rounded-lg transition-colors duration-300 ${isScrolled
                                        ? 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        : 'text-white hover:text-accent-200 hover:bg-white/10'
                                    }`} onClick={toggleMenu}>
                                    Inicio
                                </a>
                                <a href="#servicios" className={`py-3 px-3 rounded-lg transition-colors duration-300 ${isScrolled
                                        ? 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        : 'text-white hover:text-accent-200 hover:bg-white/10'
                                    }`} onClick={toggleMenu}>
                                    Servicios
                                </a>
                                <a href="#beneficios" className={`py-3 px-3 rounded-lg transition-colors duration-300 ${isScrolled
                                        ? 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        : 'text-white hover:text-accent-200 hover:bg-white/10'
                                    }`} onClick={toggleMenu}>
                                    Beneficios
                                </a>
                                <Link to="/api-docs" className={`py-3 px-3 rounded-lg transition-colors duration-300 ${isScrolled
                                        ? 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        : 'text-white hover:text-accent-200 hover:bg-white/10'
                                    }`} onClick={toggleMenu}>
                                    API
                                </Link>
                                <a href="#contacto" className={`py-3 px-3 rounded-lg transition-colors duration-300 ${isScrolled
                                        ? 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        : 'text-white hover:text-accent-200 hover:bg-white/10'
                                    }`} onClick={toggleMenu}>
                                    Contacto
                                </a>
                                <Link to="/login" className={`mt-4 py-3 px-4 rounded-lg font-medium text-center transition-all duration-300 ${isScrolled
                                        ? 'bg-primary-500 hover:bg-primary-600 text-white'
                                        : 'bg-white/20 hover:bg-white/30 text-white border border-white/30'
                                    }`} onClick={toggleMenu}>
                                    Iniciar Sesión
                                </Link>
                            </div>
                        </div>
                    )}
                </nav>
            </header>

            {/* Main Content */}
            <main>
                {/* Hero Section */}
                <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-800 text-white overflow-hidden" id="inicio">
                    {/* Background decoration */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-20 left-10 w-72 h-72 bg-accent-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
                        <div className="absolute top-40 right-10 w-72 h-72 bg-secondary-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
                        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-primary-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
                    </div>

                    <div className="container mx-auto px-4 text-center relative z-10">
                        <div className="max-w-4xl mx-auto">
                            {/* Logo */}
                            <div className="mb-8 animate-fade-in">
                                <img
                                    src={logoMafyWhite}
                                    alt="Mafy"
                                    className="h-24 w-24 lg:h-32 lg:w-32 mx-auto mb-6 hover-scale"
                                />
                            </div>

                            {/* Main heading */}
                            <h1 className="text-5xl lg:text-7xl font-bold mb-6 animate-fade-in animation-delay-200">
                                Mafy
                            </h1>

                            {/* Slogan */}
                            <div className="text-xl lg:text-2xl font-medium mb-6 text-accent-200 animate-fade-in animation-delay-400">
                                Manejo Automatizado Fiscal y Ágil — Descarga XML, factura fácil.
                            </div>

                            {/* Description */}
                            <p className="text-lg lg:text-xl text-gray-200 mb-10 max-w-3xl mx-auto animate-fade-in animation-delay-600">
                                Simplifica tu gestión fiscal con nuestra plataforma automatizada para descarga masiva del SAT y facturación electrónica.
                            </p>

                            {/* CTA Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in animation-delay-800">
                                <Link
                                    to="/login"
                                    className="btn-primary bg-accent-500 hover:bg-accent-600 text-white px-8 py-4 text-lg font-semibold shadow-glow-accent hover-lift"
                                >
                                    <CheckCircle className="inline-block w-5 h-5 mr-2" />
                                    Comenzar Ahora
                                </Link>
                                <Link
                                    to="/api-docs"
                                    className="btn-outline border-white text-white hover:bg-white hover:text-primary-600 px-8 py-4 text-lg font-semibold hover-lift"
                                >
                                    <Rocket className="inline-block w-5 h-5 mr-2" />
                                    Ver API
                                </Link>
                                <a
                                    href="#servicios"
                                    className="btn-ghost text-white hover:bg-white/10 px-8 py-4 text-lg font-semibold hover-lift"
                                >
                                    Conocer Más
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Scroll indicator */}
                    <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
                        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
                            <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
                        </div>
                    </div>
                </section>

                {/* Services Section */}
                <section className="py-20 lg:py-32 bg-white dark:bg-gray-900" id="servicios">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                                Nuestros Servicios
                            </h2>
                            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                                Soluciones integrales para tu gestión fiscal y contable
                            </p>
                        </div>

                        {/* Servicios Principales */}
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
                            <div className="card-hover p-8 group">
                                <div className="text-primary-500 mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <Download className="w-12 h-12" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                                    Descarga Masiva SAT
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-6">
                                    Descarga automática de todos tus XMLs del Portal SAT. Obtén tus facturas recibidas y emitidas de forma masiva y organizada.
                                </p>
                                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                        Autenticación con FIEL
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                        Descarga por períodos
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                        Organización automática
                                    </li>
                                </ul>
                            </div>

                            <div className="card-hover p-8 group">
                                <div className="text-primary-500 mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <FileText className="w-12 h-12" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                                    Facturación Electrónica
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-6">
                                    Sistema completo de timbrado y creación de CFDI que cumple con todas las normativas del SAT.
                                </p>
                                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                        Timbrado automático
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                        Validación en tiempo real
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                        Generación de PDF
                                    </li>
                                </ul>
                            </div>

                            <div className="card-hover p-8 group">
                                <div className="text-primary-500 mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <Shield className="w-12 h-12" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                                    Validación de CFDI
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-6">
                                    Verifica el estado y validez de tus comprobantes fiscales directamente con el SAT.
                                </p>
                                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                        Consulta estado SAT
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                        Validación de RFC
                                    </li>
                                    <li className="flex items-center">
                                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                        Verificación de firmas
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Servicios Adicionales */}
                        <div className="mb-16">
                            <h3 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
                                Servicios Especializados
                            </h3>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {[
                                    { icon: RefreshCw, title: "Cancelación de CFDI", desc: "Cancela tus comprobantes fiscales de forma individual o masiva con total seguridad." },
                                    { icon: BarChart, title: "Reportes y Exportación", desc: "Genera reportes detallados y exporta tus datos a Excel con plantillas personalizables." },
                                    { icon: Database, title: "Catálogos SAT", desc: "Acceso actualizado a todos los catálogos oficiales del SAT: formas de pago, monedas, regímenes fiscales." },
                                    { icon: DollarSign, title: "Tipos de Cambio", desc: "Consulta tipos de cambio oficiales de Banxico para tus operaciones en moneda extranjera." },
                                    { icon: Lock, title: "Gestión de Certificados", desc: "Administra tus certificados digitales FIEL y CSD de forma segura y organizada." },
                                    { icon: FileCheck, title: "Comprobantes de Retenciones", desc: "Crea, timbra y valida comprobantes de retenciones e información de pagos." }
                                ].map((service, index) => (
                                    <div key={index} className="card-hover p-6 group">
                                        <div className="text-primary-500 mb-4 group-hover:scale-110 transition-transform duration-300">
                                            <service.icon className="w-8 h-8" />
                                        </div>
                                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                            {service.title}
                                        </h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            {service.desc}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* API Card */}
                        <div className="max-w-4xl mx-auto mb-16">
                            <div className="card-hover p-8 bg-gradient-to-r from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20 border-2 border-primary-200 dark:border-primary-700">
                                <div className="flex flex-col lg:flex-row items-center gap-8">
                                    <div className="text-primary-500">
                                        <Rocket className="w-16 h-16" />
                                    </div>
                                    <div className="flex-1 text-center lg:text-left">
                                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                                            API REST Completa
                                        </h3>
                                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                                            Integra nuestros servicios en tu sistema con nuestra API REST completa y documentada.
                                        </p>
                                        <div className="flex flex-wrap gap-2 justify-center lg:justify-start mb-6">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-800 dark:text-primary-200">
                                                JWT Auth
                                            </span>
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800 dark:bg-accent-800 dark:text-accent-200">
                                                OpenAPI
                                            </span>
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-200">
                                                JSON
                                            </span>
                                        </div>
                                        <Link
                                            to="/api-docs"
                                            className="btn-primary hover-lift"
                                        >
                                            Ver Documentación →
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Herramientas Avanzadas */}
                        <div className="mb-16">
                            <h3 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">
                                Herramientas Avanzadas
                            </h3>
                            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {[
                                    { icon: Search, title: "Validaciones Avanzadas", desc: "Validación de esquemas XSD, certificados digitales y cadenas de confianza." },
                                    { icon: RefreshCw, title: "Transformaciones XML", desc: "Convierte y transforma documentos XML usando XSLT y esquemas personalizados." },
                                    { icon: Palette, title: "Renderizado de Documentos", desc: "Convierte tus CFDI a diferentes formatos: HTML, PDF, JSON con plantillas personalizables." },
                                    { icon: TrendingUp, title: "Análisis y Estadísticas", desc: "Obtén insights detallados sobre tu actividad fiscal con reportes y análisis avanzados." }
                                ].map((tool, index) => (
                                    <div key={index} className="card-hover p-6 group">
                                        <div className="text-accent-500 mb-4 group-hover:scale-110 transition-transform duration-300">
                                            <tool.icon className="w-8 h-8" />
                                        </div>
                                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                            {tool.title}
                                        </h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            {tool.desc}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Call to Action */}
                        <div className="text-center">
                            <div className="card p-8 max-w-2xl mx-auto bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                                    ¿Necesitas una solución personalizada?
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-6">
                                    Nuestro equipo puede adaptar estos servicios a las necesidades específicas de tu empresa
                                </p>
                                <Link
                                    to="/login"
                                    className="btn-primary hover-lift"
                                >
                                    Solicitar Consultoría
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section className="py-20 lg:py-32 bg-gray-50 dark:bg-gray-800" id="beneficios">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                                ¿Por qué elegir Mafy?
                            </h2>
                            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                                Beneficios que transformarán tu gestión fiscal
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[
                                { icon: Clock, title: "Ahorro de Tiempo", desc: "Automatiza procesos que normalmente tomarían horas", color: "text-blue-500" },
                                { icon: Lock, title: "Seguridad Total", desc: "Cumplimiento estricto con las normativas del SAT", color: "text-green-500" },
                                { icon: DollarSign, title: "Costo-Efectivo", desc: "Reduce costos operativos significativamente", color: "text-yellow-500" },
                                { icon: Target, title: "Precisión", desc: "Minimiza errores humanos en procesos fiscales", color: "text-red-500" },
                                { icon: Smartphone, title: "Fácil de Usar", desc: "Interfaz intuitiva y amigable para todos los usuarios", color: "text-purple-500" },
                                { icon: RefreshCw, title: "Actualizaciones", desc: "Siempre al día con los cambios del SAT", color: "text-indigo-500" }
                            ].map((benefit, index) => (
                                <div key={index} className="card-hover p-8 text-center group">
                                    <div className={`${benefit.color} mb-6 mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-white dark:bg-gray-700 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                                        <benefit.icon className="w-8 h-8" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                                        {benefit.title}
                                    </h3>
                                    <p className="text-gray-600 dark:text-gray-400">
                                        {benefit.desc}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-20 lg:py-32 bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
                    <div className="container mx-auto px-4 text-center">
                        <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                            ¿Listo para automatizar tu gestión fiscal?
                        </h2>
                        <p className="text-xl mb-10 max-w-3xl mx-auto opacity-90">
                            Únete a las empresas que ya confían en Mafy para optimizar sus procesos fiscales
                        </p>
                        <Link
                            to="/login"
                            className="btn-primary bg-accent-500 hover:bg-accent-600 px-10 py-4 text-xl font-semibold shadow-glow-accent hover-lift"
                        >
                            <CheckCircle className="inline-block w-6 h-6 mr-3" />
                            Solicitar Demo Gratuita
                        </Link>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16" id="contacto">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                        <div>
                            <div className="flex items-center space-x-3 mb-6">
                                <img
                                    src={logoMafyWhite}
                                    alt="Mafy"
                                    className="h-12 w-12"
                                />
                                <h3 className="text-2xl font-bold">Mafy</h3>
                            </div>
                            <p className="text-gray-300 mb-4">
                                Manejo Automatizado Fiscal y Ágil
                            </p>
                            <p className="text-gray-400">
                                Simplificando la gestión fiscal mexicana
                            </p>
                        </div>

                        <div>
                            <h3 className="text-xl font-bold mb-6">Contacto</h3>
                            <div className="space-y-3 text-gray-300">
                                <p>Email: info@mafy.mx</p>
                                <p>Teléfono: +52 (33) 1234-5678</p>
                                <p>Zapopan, Jalisco, México</p>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-xl font-bold mb-6">Servicios</h3>
                            <div className="space-y-3 text-gray-300">
                                <p>Descarga Masiva SAT</p>
                                <p>Facturación Electrónica</p>
                                <p>Consultoría Fiscal</p>
                                <p>API REST</p>
                            </div>
                        </div>
                    </div>

                    <hr className="border-gray-700 mb-8" />

                    <div className="text-center text-gray-400">
                        <p>&copy; 2025 Mafy. Todos los derechos reservados.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;